import { useGetBusinessPartnerById, useGetContactEmployeesByBusinessPartnerId, useGetSalesPersonByBusinessPartnerId } from '@/api/business-partner/business-partner.hooks';
import { useCart } from '@/api/carts/carts.hooks';
import { useGetOrderSummaries } from '@/api/documents/documents.hooks';
import { useItemFacets } from '@/api/items/items.hooks';
import { useCatalogFilterStore } from '@/stores/catalog-filter-store';
import { useGlobalStore } from '@/stores';
import { formatMoneyString } from '@utils/format-money-string';
import {
  Avatar,
  Box,
  ButtonBase,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  AlternateEmail,
  BuildOutlined,
  Phone,
  ReceiptLongOutlined,
  Search,
  ShoppingCartOutlined,
  StoreOutlined,
} from '@mui/icons-material';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function HeroQuickAction({
  icon,
  title,
  detail,
  onClick,
  highlight,
  isLoading,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  onClick: () => void;
  highlight?: boolean;
  isLoading?: boolean;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.08)',
        border: '1px solid',
        borderColor: highlight
          ? 'rgba(255,193,7,0.4)'
          : 'rgba(255,255,255,0.1)',
        textAlign: 'left',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: 'rgba(255,255,255,0.14)',
          borderColor: highlight
            ? 'rgba(255,193,7,0.6)'
            : 'rgba(255,255,255,0.25)',
        },
      }}
    >
      <Box
        sx={{
          color: highlight ? '#ffc107' : 'rgba(255,255,255,0.7)',
          display: 'flex',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '0.78rem',
            lineHeight: 1.3,
            color: 'white',
          }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton
            width={60}
            height={12}
            sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
          />
        ) : (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              lineHeight: 1.2,
              color: 'rgba(255,255,255,0.5)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {detail}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

export function HeroSection() {
  const [searchValue, setSearchValue] = useState('');
  const { setSearchText, clearAll } = useCatalogFilterStore();
  const navigate = useNavigate();

  const { contactEmployeeId } = useGlobalStore();
  const { data: businessPartner } = useGetBusinessPartnerById();
  const { data: contactEmployees, isPending: isCePending } =
    useGetContactEmployeesByBusinessPartnerId();
  const { data: salesPerson, isPending: isSpPending } =
    useGetSalesPersonByBusinessPartnerId();
  const { data: cart } = useCart({ staleTime: 5 * 60 * 1000 });
  const { data: orderData } = useGetOrderSummaries(1);
  const { data: facets } = useItemFacets(
    businessPartner?.defaultPriceListId
      ? { priceListId: businessPartner.defaultPriceListId }
      : undefined,
  );

  const currentContact = contactEmployees?.find(
    (ce) => ce.id === contactEmployeeId,
  );
  const contactName = currentContact
    ? `${currentContact.firstName} ${currentContact.lastName}`
    : null;

  const handleSearch = () => {
    if (!searchValue.trim()) return;
    clearAll();
    setSearchText(searchValue.trim());
    navigate('/bp/c/items');
  };

  const fullName =
    salesPerson?.firstName && salesPerson?.lastName
      ? `${salesPerson.firstName} ${salesPerson.lastName}`
      : null;
  const phone = salesPerson?.officePhone || salesPerson?.mobilePhone;

  const cartCount = cart?.cartItems?.length ?? 0;
  const cartTotal = cart?.priceCalculation?.totalNetAmount ?? 0;
  const totalItems = facets?.totalItems ?? 0;
  const totalOrders = orderData?.total ?? 0;

  const cartDetail =
    cartCount > 0
      ? `${cartCount} Artikel (${formatMoneyString(cartTotal)})`
      : 'Leer';

  return (
    <Box
      sx={{
        mx: { xs: -1, sm: -2.5 },
        mt: { xs: -1.5, sm: -2.5 },
        px: { xs: 2, sm: 4 },
        pt: { xs: 4, sm: 5 },
        pb: { xs: 3.5, sm: 4.5 },
        background:
          'linear-gradient(135deg, #02344C 0%, #063f5c 50%, #04516F 100%)',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Subtle geometric pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.04,
          backgroundImage:
            'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', maxWidth: 1200, mx: 'auto' }}>
        {/* Greeting + Contact Person */}
        <Grid container spacing={3} sx={{ alignItems: "center" }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                color: 'rgba(255,255,255,0.75)',
                mb: 0.5,
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              Willkommen zurück,
            </Typography>
            {isCePending ? (
              <Skeleton
                width={280}
                height={40}
                sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
              />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  lineHeight: 1.2,
                }}
              >
                {contactName ?? businessPartner?.name}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 2.5,
                px: 2,
                py: 1.5,
                backdropFilter: 'blur(8px)',
              }}
            >
              {isSpPending ? (
                <>
                  <Skeleton
                    variant="circular"
                    width={44}
                    height={44}
                    sx={{ bgcolor: 'rgba(255,255,255,0.12)', flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton
                      width={120}
                      height={18}
                      sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
                    />
                    <Skeleton
                      width={160}
                      height={14}
                      sx={{ bgcolor: 'rgba(255,255,255,0.12)', mt: 0.5 }}
                    />
                  </Box>
                </>
              ) : fullName ? (
                <>
                  <Avatar
                    alt={fullName}
                    src="/cb700561-0606-47e5-9b2c-b385e185327a.jpeg"
                    sx={{
                      width: 44,
                      height: 44,
                      border: '2px solid rgba(255,255,255,0.2)',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.55)',
                        fontSize: '0.65rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        lineHeight: 1,
                      }}
                    >
                      Ihr Ansprechpartner
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'white', fontWeight: 700, lineHeight: 1.3 }}
                    >
                      {fullName}
                    </Typography>
                    <Stack
                      direction="column"
                      spacing={0.25}
                      sx={{ mt: 0.25 }}
                    >
                      {salesPerson?.email && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          component="a"
                          href={`mailto:${salesPerson.email}`}
                          sx={{
                            alignItems: "center",
                            color: 'rgba(255,255,255,0.6)',
                            textDecoration: 'none',
                            '&:hover': { color: 'white' },
                            transition: 'color 0.15s',
                          }}
                        >
                          <AlternateEmail sx={{ fontSize: 13 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {salesPerson.email}
                          </Typography>
                        </Stack>
                      )}
                      {phone && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          component="a"
                          href={`tel:${phone}`}
                          sx={{
                            alignItems: "center",
                            color: 'rgba(255,255,255,0.6)',
                            textDecoration: 'none',
                            '&:hover': { color: 'white' },
                            transition: 'color 0.15s',
                          }}
                        >
                          <Phone sx={{ fontSize: 13 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {phone}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.5)' }}
                >
                  Kein Ansprechpartner zugewiesen
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <Box sx={{ mt: 3.5 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Artikelnummer, Artikelname oder EAN eingeben..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue.trim() && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.95)',
                borderRadius: 3,
                fontSize: '1.05rem',
                transition: 'box-shadow 0.2s',
                '& fieldset': { border: 'none' },
                '&:hover': {
                  boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 6px 32px rgba(0,0,0,0.2)',
                },
              },
            }}
          />
        </Box>

        {/* Quick Actions */}
        <Grid container spacing={1.5} sx={{ mt: 2.5 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <HeroQuickAction
              icon={<StoreOutlined fontSize="small" />}
              title="Shop"
              detail={`${totalItems.toLocaleString('de-DE')} Artikel`}
              onClick={() => navigate('/bp/c/items')}
              isLoading={!facets}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <HeroQuickAction
              icon={<ShoppingCartOutlined fontSize="small" />}
              title="Warenkorb"
              detail={cartDetail}
              onClick={() => navigate('/bp/c/cart')}
              highlight={cartCount > 0}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <HeroQuickAction
              icon={<ReceiptLongOutlined fontSize="small" />}
              title="Bestellungen"
              detail={`${totalOrders} Bestellungen`}
              onClick={() => navigate('/bp/c/orders')}
              isLoading={!orderData}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <HeroQuickAction
              icon={<BuildOutlined fontSize="small" />}
              title="CTO-Finder"
              detail="Konfigurieren & bestellen"
              onClick={() => navigate('/bp/c/cto')}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
